import { type MouseEvent, useLayoutEffect, useRef, useState } from "react";
import { eventNames } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

const REGION = "Shop Window Footer";
// In the list window (ListShopWindow)
const LIST_FOOTER =
  "tw:[.list-shop-window_&]:mt-[10px] tw:[.list-shop-window_&]:flex tw:[.list-shop-window_&]:shrink-0 tw:[.list-shop-window_&]:flex-col tw:[.list-shop-window_&]:justify-end tw:[.portrait_.list-shop-window_&]:mb-[10px]";
const LIST_CONTROLS = "tw:[.list-shop-window_&]:[padding:0_16px_0_16px]";
// In the method window (MethodShopWindow)
const METHOD_FOOTER =
  "tw:[.landscape_.method-shop-window_&]:mt-[10px] tw:[.landscape_.method-shop-window_&]:flex tw:[.landscape_.method-shop-window_&]:shrink-0 tw:[.landscape_.method-shop-window_&]:flex-col tw:[.landscape_.method-shop-window_&]:justify-end tw:[.portrait_.method-shop-window_&]:order-3 tw:[.portrait_.method-shop-window_&]:[margin:5px_10px_5px_10px] tw:[.portrait_.method-shop-window_&]:w-auto tw:[.portrait_.method-shop-window_&]:flex-col tw:[.portrait_.method-shop-window_&]:items-stretch";
const METHOD_CONTROLS =
  "tw:[.landscape_.method-shop-window_&]:flex tw:[.landscape_.method-shop-window_&]:w-full tw:[.landscape_.method-shop-window_&]:[padding:0_16px_0_16px] tw:[.portrait_.method-shop-window_&]:block tw:[.portrait_.method-shop-window_&]:w-auto tw:[.portrait_.method-shop-window_&]:[padding:0_16px_0_16px]";
const METHOD_CTA =
  "tw:[.landscape_.method-shop-window_&]:relative tw:[.landscape_.method-shop-window_&]:mr-[0px] tw:[.landscape_.method-shop-window_&]:ml-[0px] tw:[.landscape_.method-shop-window_&]:grow tw:[.landscape_.method-shop-window_&]:font-[400] tw:[.portrait_.method-shop-window_&]:block tw:[.portrait_.method-shop-window_&]:w-full";
const LIST_CTA =
  "tw:[.list-shop-window_&]:relative tw:[.list-shop-window_&]:mr-[0px] tw:[.list-shop-window_&]:ml-[0px] tw:[.list-shop-window_&]:w-full tw:[.list-shop-window_&]:font-[400]";

interface CtaLink {
  label?: { click?: { title?: string } };
}

interface Links {
  cta?: unknown;
}

type ShopWindowFooterProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  links?: Links;
  isSelected?: boolean;
  isActive?: boolean;
  isHidden?: boolean;
  hasButton?: boolean;
  pagination?: unknown;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

export default function ShopWindowFooter({
  experienceState,
  config,
  links: linksProp,
  isSelected,
  isActive,
  isHidden: isHiddenProp,
  hasButton,
  pagination: paginationProp,
  onApplicationEvent,
}: ShopWindowFooterProps) {
  // Button key counter; bumped during render, as the class did.
  const [keys] = useState(() => ({ ctaId: 1 }));
  const cta = useRef<unknown>(0);
  const didMount = useRef(false);

  // componentDidUpdate: runs on every commit except the first.
  useLayoutEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const clusterIndex = config.clusterArticleIndexAt(
      experienceState.experienceData,
      config.index,
    );

    if (clusterIndex.cta !== cta.current) {
      if (onApplicationEvent) {
        onApplicationEvent({
          region: REGION,
          name: eventNames.CTA_DATA_DID_UPDATE,
          cta: clusterIndex.cta,
        });
      }

      cta.current = clusterIndex.cta;
    }
  });

  const onCtaButtonClick = (e: MouseEvent) => {
    const experienceData = experienceState.experienceData;
    const clusterIndex = config.clusterArticleIndexAt(
      experienceData,
      config.index,
    );
    if (linksProp) {
      clusterIndex.cta = linksProp.cta;
    }

    if (onApplicationEvent) {
      if (clusterIndex && clusterIndex.cta) {
        onApplicationEvent({
          region: REGION,
          name: eventNames.LINK,
          clusterName: clusterIndex.clusterName,
          cindex: clusterIndex.cindex,
          index: clusterIndex.index,
          link: clusterIndex.cta,
        });
      }
    }

    e.preventDefault();
  };

  const getLinks = (): Links | undefined => {
    if (linksProp) {
      return linksProp;
    }
    const clusterIndex = config.clusterArticleIndexAt(
      experienceState.experienceData,
      config.index,
    );
    if (clusterIndex.cta) {
      return { cta: clusterIndex.cta };
    }
  };

  const getStandardControls = (links: Links | undefined) => {
    const selected = !!isSelected;
    const active = !!isActive;

    const selectedClass = selected ? " selected" : "";
    const hoverClass = !selected && active ? " hover" : "";
    const activeClass = active ? " active" : " inactive";
    const label = links && links.cta ? (links.cta as CtaLink).label : null;
    const title = label && label.click ? label.click.title : null;

    return (
      <div
        className={`shop-window-footer-controls ${LIST_CONTROLS} ${METHOD_CONTROLS}`}
      >
        <button
          key={`cta-btn-${keys.ctaId}`}
          type="button"
          className={`cta-btn button type-1 text wide ${LIST_CTA} ${METHOD_CTA}${activeClass}${selectedClass}${hoverClass}`}
          onClick={onCtaButtonClick}
        >
          <span>{title}</span>
        </button>
      </div>
    );
  };

  const applicationData = experienceState.applicationData;
  const links = getLinks();
  const hasLink = !!(links && links.cta);
  const isHidden = !!isHiddenProp;
  const pagination = paginationProp ? (
    <div className="swiper-pagination" />
  ) : null;

  if (links && links.cta && cta.current !== links.cta) {
    keys.ctaId++;
  }

  let controls = null;

  if (hasLink && (applicationData.isLandscape() || hasButton)) {
    controls = getStandardControls(links);
  }

  let style;
  if (isHidden) {
    style = { display: "none" };
  }

  return (
    <div
      className={`shop-window-footer tw:w-full ${LIST_FOOTER} ${METHOD_FOOTER}`}
      style={style}
    >
      {pagination}
      {controls}
    </div>
  );
}
